import React, { useEffect, useId, useRef } from 'react';

interface ComposerPopoverProps {
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  align?: 'left' | 'right';
  trigger: React.ReactNode;
  children: React.ReactNode;
}

/** Shared motion, dismissal and keyboard behaviour for the composer controls. */
const ComposerPopover: React.FC<ComposerPopoverProps> = ({
  label, open, onOpenChange, disabled = false, align = 'left', trigger, children
}) => {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const focusLastRef = useRef(false);

  const items = () => Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>(
    '[role^="menuitem"]:not(:disabled)'
  ) || []);

  useEffect(() => {
    panelRef.current?.toggleAttribute('inert', !open);
    if (!open) return;
    const focusFrame = window.requestAnimationFrame(() => {
      const options = items();
      (focusLastRef.current ? options.at(-1) : options[0])?.focus({ preventScroll: true });
      focusLastRef.current = false;
    });
    const dismiss = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('pointerdown', dismiss);
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    if (disabled && open) onOpenChange(false);
  }, [disabled, open, onOpenChange]);

  const closeWithFocus = () => {
    onOpenChange(false);
    buttonRef.current?.focus({ preventScroll: true });
  };

  return (
    <div ref={rootRef} className="relative shrink-0" onBlur={(event) => {
      if (open && !event.currentTarget.contains(event.relatedTarget as Node)) onOpenChange(false);
    }} onKeyDown={(event) => {
      if (event.key === 'Escape' && open) {
        event.preventDefault();
        closeWithFocus();
      }
    }}>
      <button ref={buttonRef} type="button" aria-label={label} title={label}
        aria-haspopup="menu" aria-expanded={open} aria-controls={menuId} disabled={disabled}
        onClick={() => onOpenChange(!open)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            focusLastRef.current = event.key === 'ArrowUp';
            onOpenChange(true);
          }
        }}
        className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${open ? 'bg-juris-soft text-juris' : 'text-muted hover:bg-raised hover:text-juris'}`}>
        {trigger}
      </button>
      <div ref={panelRef} id={menuId} role="menu" aria-label={label} aria-hidden={!open}
        data-open={open} className={`composer-popover custom-scrollbar absolute bottom-[calc(100%+0.75rem)] z-50 max-h-[calc(100dvh-11rem)] w-60 max-w-[calc(100vw-5.5rem)] overflow-y-auto rounded-2xl border border-line bg-raised p-2 shadow-2xl shadow-black/50 ${align === 'right' ? 'right-0 origin-bottom-right' : 'left-0 origin-bottom-left'}`}
        onClick={(event) => {
          if ((event.target as HTMLElement).closest('[role^="menuitem"]:not(:disabled)')) closeWithFocus();
        }} onKeyDown={(event) => {
          const options = items();
          const current = options.indexOf(document.activeElement as HTMLButtonElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
            : event.key === 'ArrowDown' ? (current + 1) % options.length
              : event.key === 'ArrowUp' ? (current - 1 + options.length) % options.length : null;
          if (next !== null) {
            event.preventDefault();
            options[next]?.focus();
          }
        }}>
        {children}
      </div>
    </div>
  );
};

export default ComposerPopover;
